import { useState, useEffect, useCallback, useRef } from "react";
import {
    Home,
    Upload,
    FolderPlus,
    Folder,
    File,
    FileText,
    FileImage,
    Film,
    Music,
    Archive,
    FileSpreadsheet,
    Code,
    Link,
    Pencil,
    Download,
    Trash2,
    FolderOpen,
    User,
    MoveRight,
    Package,
    LayoutGrid,
    List,
    ChevronDown,
    ChevronUp,
    MoreHorizontal,
    Eye,
    Search,
    X,
    Filter,
    Cross,
    Menu,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
    listFiles,
    createFolder,
    renameItem,
    moveItem,
    deleteItem,
    createShareLink,
    revokeShareLink,
    getDownloadUrl,
    getShareUrl,
    getBreadcrumb,
    getFileInfo,
    uploadFile,
} from "../../utils/api";
import { formatBytes, formatDate } from "../../utils/fileHelpers";
import ContextMenu from "../../components/ContextMenu/ContextMenu";
import Modal from "../../components/Modal/Modal";
import FolderPicker from "../../components/FolderPicker/FolderPicker";

import "./ExplorerPage.css";

const TYPE_OPTIONS = [
    { value: "all", label: "All types" },
    { value: "image", label: "Images" },
    { value: "video", label: "Videos" },
    { value: "audio", label: "Audio" },
    { value: "pdf", label: "PDFs" },
    { value: "doc", label: "Documents" },
    { value: "sheet", label: "Spreadsheets" },
    { value: "code", label: "Code" },
    { value: "zip", label: "Archives" },
    { value: "file", label: "Other" },
];

function getFileIcon(mimeType, name, size) {
    const s = size ?? 20;
    const ext = name ? name.split(".").pop().toLowerCase() : "";
    if (mimeType?.includes("pdf") || ext === "pdf")
        return <FileText size={s} />;
    if (
        mimeType?.includes("image") ||
        ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)
    )
        return <FileImage size={s} />;
    if (
        mimeType?.includes("video") ||
        ["mp4", "mov", "avi", "mkv"].includes(ext)
    )
        return <Film size={s} />;
    if (
        mimeType?.includes("audio") ||
        ["mp3", "wav", "flac", "aac"].includes(ext)
    )
        return <Music size={s} />;
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext))
        return <Archive size={s} />;
    if (["doc", "docx"].includes(ext)) return <FileText size={s} />;
    if (["xls", "xlsx", "csv"].includes(ext))
        return <FileSpreadsheet size={s} />;
    if (
        ["js", "ts", "py", "java", "cpp", "c", "html", "css", "json"].includes(
            ext,
        )
    )
        return <Code size={s} />;
    return <File size={s} />;
}

function getFileType(mimeType, name) {
    const ext = name ? name.split(".").pop().toLowerCase() : "";
    if (mimeType?.includes("pdf") || ext === "pdf") return "pdf";
    if (
        mimeType?.includes("image") ||
        ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)
    )
        return "image";
    if (
        mimeType?.includes("video") ||
        ["mp4", "mov", "avi", "mkv"].includes(ext)
    )
        return "video";
    if (
        mimeType?.includes("audio") ||
        ["mp3", "wav", "flac", "aac"].includes(ext)
    )
        return "audio";
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "zip";
    if (["doc", "docx"].includes(ext)) return "doc";
    if (["xls", "xlsx", "csv"].includes(ext)) return "sheet";
    if (
        ["js", "ts", "py", "java", "cpp", "c", "html", "css", "json"].includes(
            ext,
        )
    )
        return "code";
    return "file";
}

export default function ExplorerPage({ onViewBundles }) {
    const { user, logout } = useAuth();
    const [currentFolderId, setCurrentFolderId] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("folder") || null;
    });
    const [items, setItems] = useState([]);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [modal, setModal] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [shareInfo, setShareInfo] = useState(null);
    const [toast, setToast] = useState(null);
    const [uploads, setUploads] = useState([]);
    const [viewMode, setViewMode] = useState("grid");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [draggingItem, setDraggingItem] = useState(null);
    const [dragOverFolder, setDragOverFolder] = useState(null);
    const [dragOverCrumb, setDragOverCrumb] = useState(null);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const fileInputRef = useRef();
    const searchRef = useRef();

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listFiles(currentFolderId);
            setItems(res.data);
        } catch {
            showToast("Failed to load files", "error");
        } finally {
            setLoading(false);
        }
    }, [currentFolderId]);

    const loadBreadcrumbs = useCallback(async () => {
        if (!currentFolderId) {
            setBreadcrumbs([]);
            return;
        }
        try {
            const res = await getBreadcrumb(currentFolderId);
            setBreadcrumbs(res.data);
        } catch {}
    }, [currentFolderId]);

    useEffect(() => {
        loadItems();
        loadBreadcrumbs();
        setSelected(null);
        setSearch("");
        setTypeFilter("all");
    }, [loadItems, loadBreadcrumbs]);

    const handleNavigate = (folderId) => setCurrentFolderId(folderId);

    const openMenu = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        setSelected(item);
        setContextMenu({ x: e.clientX, y: e.clientY, item });
    };

    const openMenuFromBtn = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setSelected(item);
        setContextMenu({ x: rect.left, y: rect.bottom + 4, item });
    };

    const handleUploadClick = () => fileInputRef.current.click();

    const processFiles = async (files, targetFolderId) => {
        const destination = targetFolderId ?? currentFolderId;
        for (const file of files) {
            const id = Date.now() + Math.random();
            setUploads((prev) => [
                ...prev,
                { id, name: file.name, progress: 0, done: false, error: false },
            ]);
            try {
                await uploadFile(file, destination, (p) => {
                    setUploads((prev) =>
                        prev.map((u) =>
                            u.id === id ? { ...u, progress: p } : u,
                        ),
                    );
                });
                setUploads((prev) =>
                    prev.map((u) =>
                        u.id === id ? { ...u, done: true, progress: 100 } : u,
                    ),
                );
                setTimeout(
                    () => setUploads((prev) => prev.filter((u) => u.id !== id)),
                    2000,
                );
                loadItems();
            } catch {
                setUploads((prev) =>
                    prev.map((u) => (u.id === id ? { ...u, error: true } : u)),
                );
            }
        }
    };

    const handleFileInputChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length) processFiles(files);
        e.target.value = "";
    };

    const handleGridDragOver = (e) => {
        if (draggingItem) return;
        e.preventDefault();
    };
    const handleGridDrop = (e) => {
        if (draggingItem) return;
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        if (files.length) processFiles(files);
    };
    const handleItemDragStart = (e, item) => {
        setDraggingItem(item);
        e.dataTransfer.effectAllowed = "move";
    };
    const handleItemDragEnd = () => {
        setDraggingItem(null);
        setDragOverFolder(null);
        setDragOverCrumb(null);
    };

    const handleFolderDragOver = (e, folder) => {
        if (!draggingItem || draggingItem._id === folder._id) return;
        e.preventDefault();
        e.stopPropagation();
        setDragOverFolder(folder._id);
    };
    const handleFolderDragLeave = () => setDragOverFolder(null);
    const handleFolderDrop = async (e, folder) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverFolder(null);
        if (!draggingItem || draggingItem._id === folder._id) return;
        const moving = draggingItem;
        setDraggingItem(null);
        try {
            await moveItem(moving._id, folder._id);
            showToast(`Moved "${moving.name}" into "${folder.name}"`);
            loadItems();
        } catch {
            showToast("Move failed", "error");
        }
    };

    const allCrumbs = [{ _id: null, name: "My Files" }, ...breadcrumbs];
    const handleCrumbDragOver = (e, i) => {
        if (!draggingItem || i === allCrumbs.length - 1) return;
        e.preventDefault();
        e.stopPropagation();
        setDragOverCrumb(i);
    };
    const handleCrumbDragLeave = () => setDragOverCrumb(null);
    const handleCrumbDrop = async (e, crumb, i) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverCrumb(null);
        if (!draggingItem || i === allCrumbs.length - 1) return;
        const moving = draggingItem;
        setDraggingItem(null);
        try {
            await moveItem(moving._id, crumb._id);
            showToast(`Moved "${moving.name}" to "${crumb.name}"`);
            loadItems();
        } catch {
            showToast("Move failed", "error");
        }
    };

    const handleSort = (col) => {
        if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else {
            setSortBy(col);
            setSortDir("asc");
        }
    };

    const filteredItems = items.filter((item) => {
        const matchesSearch =
            !search.trim() ||
            item.name.toLowerCase().includes(search.toLowerCase());
        const matchesType =
            typeFilter === "all" ||
            (item.type === "folder" && item.childTypes?.includes(typeFilter)) ||
            (item.type === "file" &&
                getFileType(item.mimeType, item.name) === typeFilter);
        return matchesSearch && matchesType;
    });

    const sortedItems = [...filteredItems].sort((a, b) => {
        if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
        let av, bv;
        if (sortBy === "size") {
            av = a.size ?? 0;
            bv = b.size ?? 0;
        } else if (sortBy === "date") {
            av = new Date(a.createdAt);
            bv = new Date(b.createdAt);
        } else {
            av = a.name.toLowerCase();
            bv = b.name.toLowerCase();
        }
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
    });

    const SortIcon = ({ col }) =>
        sortBy === col ? (
            sortDir === "asc" ? (
                <ChevronUp size={13} />
            ) : (
                <ChevronDown size={13} />
            )
        ) : null;

    const isFiltered = search.trim() || typeFilter !== "all";

    const openRename = (item) => {
        setInputValue(item.name);
        setModal({ type: "rename", item });
    };
    const openNewFolder = () => {
        setInputValue("");
        setModal({ type: "newFolder" });
    };
    const openShare = (item) => {
        setModal({ type: "share", item });
        setShareInfo({ token: item.shareToken, enabled: item.shareEnabled });
    };
    const openMove = (item) => setModal({ type: "move", item });

    const handleMoveSelect = async ({ id, name }) => {
        const item = modal.item;
        setModal(null);
        if (id === currentFolderId) {
            showToast("Already in that folder");
            return;
        }
        try {
            await moveItem(item._id, id);
            showToast(`Moved "${item.name}" to "${name}"`);
            loadItems();
        } catch {
            showToast("Move failed", "error");
        }
    };

    const handleRename = async () => {
        if (!inputValue.trim()) return;
        try {
            await renameItem(modal.item._id, inputValue.trim());
            showToast("Renamed");
            setModal(null);
            loadItems();
        } catch {
            showToast("Rename failed", "error");
        }
    };

    const handleNewFolder = async () => {
        if (!inputValue.trim()) return;
        try {
            await createFolder(inputValue.trim(), currentFolderId);
            showToast("Folder created");
            setModal(null);
            loadItems();
        } catch {
            showToast("Failed to create folder", "error");
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`))
            return;
        try {
            await deleteItem(item._id);
            showToast("Deleted");
            loadItems();
        } catch {
            showToast("Delete failed", "error");
        }
    };

    const handleDownload = (item) => {
        const token = localStorage.getItem("token");
        fetch(getDownloadUrl(item._id), {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.blob())
            .then((blob) => {
                const a = document.createElement("a");
                const url = URL.createObjectURL(blob);
                a.href = url;
                a.download = item.name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            });
    };

    const handlePreview = (item) => {
        const token = localStorage.getItem("token");
        fetch(getDownloadUrl(item._id), {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.blob())
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            });
    };

    const handleGenerateShare = async () => {
        try {
            const res = await createShareLink(modal.item._id);
            setShareInfo({ token: res.data.shareToken, enabled: true });
            loadItems();
        } catch {
            showToast("Share failed", "error");
        }
    };
    const handleRevokeShare = async () => {
        try {
            await revokeShareLink(modal.item._id);
            setShareInfo((p) => ({ ...p, enabled: false }));
            loadItems();
        } catch {
            showToast("Failed to revoke", "error");
        }
    };
    const handleCopyShareLink = () => {
        navigator.clipboard.writeText(getShareUrl(shareInfo.token));
        showToast("Link copied!");
    };

    const getMenuItems = (item) => {
        const m = [
            {
                icon: <Pencil size={16} />,
                label: "Rename",
                onClick: () => openRename(item),
            },
            {
                icon: <MoveRight size={16} />,
                label: "Move to…",
                onClick: () => openMove(item),
            },
        ];
        if (item.type === "file") {
            const fileType = getFileType(item.mimeType, item.name);

            m.push({
                icon: <Eye size={16} />,
                label: fileType === "pdf" ? "Open" : "Preview",
                onClick: () => handleOpenItem(item),
            });

            m.push({
                icon: <Download size={16} />,
                label: "Download",
                onClick: () => handleDownload(item),
            });

            m.push({
                icon: <Link size={16} />,
                label: "Share link",
                onClick: () => openShare(item),
            });
        }
        m.push("divider");
        m.push({
            icon: <Trash2 size={16} />,
            label: "Delete",
            danger: true,
            onClick: () => handleDelete(item),
        });
        return m;
    };

    const handleOpenItem = (item) => {
        if (item.type === "folder") {
            handleNavigate(item._id);
            return;
        }

        if (getFileType(item.mimeType, item.name) === "pdf") {
            window.location.href = `/files/${item._id}/view`;
            return;
        }

        handlePreview(item);
    };

    const itemProps = (item) => ({
        draggable: true,
        onDragStart: (e) => handleItemDragStart(e, item),
        onDragEnd: handleItemDragEnd,
        onContextMenu: (e) => openMenu(e, item),
        onClick: (e) => {
            e.stopPropagation();
            setSelected(item);
        },
        onDoubleClick: () => handleOpenItem(item),
        ...(item.type === "folder"
            ? {
                  onDragOver: (e) => handleFolderDragOver(e, item),
                  onDragLeave: handleFolderDragLeave,
                  onDrop: (e) => handleFolderDrop(e, item),
              }
            : {}),
    });

    const emptyMessage = isFiltered
        ? `No results${search ? ` for "${search}"` : ""}`
        : "This folder is empty";

    return (
        <div className="explorer">
            <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFileInputChange}
            />

            {/* Mobile backdrop */}
            <div
                className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`}
                onClick={() => setSidebarOpen(false)}
            />

            <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
                <div className="sidebar-logo">
                    <div
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: 8,
                            backgroundColor: "#00a854",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Cross size={15} color="#fff" />
                    </div>
                    <span>Brian Balanoff</span>
                    <button
                        className="sidebar-close-btn"
                        onClick={() => setSidebarOpen(false)}
                        title="Close menu"
                    >
                        <X size={18} />
                    </button>
                </div>
                <nav className="sidebar-nav">
                    <button
                        className={`nav-item ${currentFolderId === null ? "active" : ""}`}
                        onClick={() => {
                            handleNavigate(null);
                            setSidebarOpen(false);
                        }}
                    >
                        <Home size={18} /> My Files
                    </button>
                    <button
                        className="nav-item"
                        onClick={() => {
                            onViewBundles();
                            setSidebarOpen(false);
                        }}
                    >
                        <Package size={18} /> Bundles
                    </button>
                </nav>
                <div className="sidebar-actions">
                    <button className="btn-upload" onClick={handleUploadClick}>
                        <Upload size={16} /> Upload Files
                    </button>
                    <button className="btn-folder" onClick={openNewFolder}>
                        <FolderPlus size={17} /> New Folder
                    </button>
                </div>
                <div className="sidebar-footer">
                    <span className="sidebar-user">
                        <User size={15} /> {user?.username}
                    </span>
                    <button className="btn-logout" onClick={logout}>
                        Sign out
                    </button>
                </div>
            </aside>

            <main
                className="explorer-main"
                onClick={() => {
                    setSelected(null);
                    setContextMenu(null);
                }}
            >
                <div className="explorer-toolbar">
                    <button
                        className="mobile-menu-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSidebarOpen(true);
                        }}
                        title="Open menu"
                    >
                        <Menu size={18} />
                    </button>
                    <div className="breadcrumbs">
                        {allCrumbs.map((crumb, i) => (
                            <span
                                key={i}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                }}
                            >
                                {i > 0 && <span className="crumb-sep">/</span>}
                                <button
                                    className={`crumb ${i === allCrumbs.length - 1 ? "active" : ""} ${dragOverCrumb === i ? "crumb-drag-over" : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleNavigate(crumb._id);
                                    }}
                                    onDragOver={(e) =>
                                        handleCrumbDragOver(e, i)
                                    }
                                    onDragLeave={handleCrumbDragLeave}
                                    onDrop={(e) => handleCrumbDrop(e, crumb, i)}
                                >
                                    {crumb.name}
                                </button>
                            </span>
                        ))}
                    </div>

                    <div className="toolbar-right">
                        {/* Search */}
                        <div className="toolbar-search">
                            <Search size={14} className="toolbar-search-icon" />
                            <input
                                ref={searchRef}
                                className="toolbar-search-input"
                                placeholder="Search files…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            {search && (
                                <button
                                    className="toolbar-search-clear"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSearch("");
                                        searchRef.current?.focus();
                                    }}
                                >
                                    <X size={13} />
                                </button>
                            )}
                        </div>

                        {/* Type filter */}
                        <div
                            className={`toolbar-filter ${typeFilter !== "all" ? "active" : ""}`}
                        >
                            <Filter size={13} className="toolbar-filter-icon" />
                            <select
                                className="toolbar-filter-select"
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* View toggle */}
                        <div className="view-toggle">
                            <button
                                className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                                onClick={() => setViewMode("grid")}
                                title="Grid view"
                            >
                                <LayoutGrid size={16} />
                            </button>
                            <button
                                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                                onClick={() => setViewMode("list")}
                                title="List view"
                            >
                                <List size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                {uploads.length > 0 && (
                    <div className="upload-progress-list">
                        {uploads.map((u) => (
                            <div
                                key={u.id}
                                className={`upload-progress-item ${u.done ? "done" : ""} ${u.error ? "error" : ""}`}
                            >
                                <div className="upload-progress-name">
                                    {u.name}
                                </div>
                                {!u.error ? (
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill"
                                            style={{ width: `${u.progress}%` }}
                                        />
                                    </div>
                                ) : (
                                    <div className="upload-error">
                                        Upload failed
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {viewMode === "grid" && (
                    <div
                        className="file-grid"
                        onContextMenu={(e) => e.preventDefault()}
                        onDragOver={handleGridDragOver}
                        onDrop={handleGridDrop}
                    >
                        {loading ? (
                            <div className="empty-state">
                                <div>Loading…</div>
                            </div>
                        ) : sortedItems.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <FolderOpen size={64} />
                                </div>
                                <div>{emptyMessage}</div>
                                {!isFiltered && (
                                    <div className="empty-hint">
                                        Upload files or create a folder to get
                                        started
                                    </div>
                                )}
                            </div>
                        ) : (
                            sortedItems.map((item) => (
                                <div
                                    key={item._id}
                                    className={`file-item ${selected?._id === item._id ? "selected" : ""} ${item.type} ${dragOverFolder === item._id ? "drag-over" : ""}`}
                                    data-type={
                                        item.type === "file"
                                            ? getFileType(
                                                  item.mimeType,
                                                  item.name,
                                              )
                                            : undefined
                                    }
                                    {...itemProps(item)}
                                >
                                    <button
                                        className="file-item-menu-btn"
                                        onClick={(e) =>
                                            openMenuFromBtn(e, item)
                                        }
                                        title="More options"
                                    >
                                        <MoreHorizontal size={15} />
                                    </button>
                                    <div className="file-icon">
                                        {item.type === "folder" ? (
                                            <Folder size={52} />
                                        ) : (
                                            getFileIcon(
                                                item.mimeType,
                                                item.name,
                                                52,
                                            )
                                        )}
                                    </div>
                                    <div
                                        className="file-name"
                                        title={item.name}
                                    >
                                        {item.name}
                                    </div>
                                    {item.type === "file" && (
                                        <div className="file-meta">
                                            {formatBytes(item.size)}
                                        </div>
                                    )}
                                    {item.shareEnabled && (
                                        <div
                                            className="share-badge"
                                            title="Shared"
                                        >
                                            <Link size={14} />
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}

                {viewMode === "list" && (
                    <div
                        className="file-list-view"
                        onContextMenu={(e) => e.preventDefault()}
                        onDragOver={handleGridDragOver}
                        onDrop={handleGridDrop}
                    >
                        {loading ? (
                            <div className="empty-state">
                                <div>Loading…</div>
                            </div>
                        ) : sortedItems.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <FolderOpen size={64} />
                                </div>
                                <div>{emptyMessage}</div>
                                {!isFiltered && (
                                    <div className="empty-hint">
                                        Upload files or create a folder to get
                                        started
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="list-header">
                                    <div className="list-col-icon" />
                                    <button
                                        className="list-col-name list-sort-btn"
                                        onClick={() => handleSort("name")}
                                    >
                                        Name <SortIcon col="name" />
                                    </button>
                                    <button className="list-col-type list-sort-btn">
                                        Type
                                    </button>
                                    <button
                                        className="list-col-size list-sort-btn"
                                        onClick={() => handleSort("size")}
                                    >
                                        Size <SortIcon col="size" />
                                    </button>
                                    <button
                                        className="list-col-date list-sort-btn"
                                        onClick={() => handleSort("date")}
                                    >
                                        Date <SortIcon col="date" />
                                    </button>
                                    <div className="list-col-actions" />
                                </div>
                                {sortedItems.map((item) => (
                                    <div
                                        key={item._id}
                                        className={`list-row ${item.type} ${selected?._id === item._id ? "selected" : ""} ${dragOverFolder === item._id ? "drag-over" : ""}`}
                                        data-type={
                                            item.type === "file"
                                                ? getFileType(
                                                      item.mimeType,
                                                      item.name,
                                                  )
                                                : undefined
                                        }
                                        {...itemProps(item)}
                                    >
                                        <div className="list-col-icon list-item-icon">
                                            {item.type === "folder" ? (
                                                <Folder size={20} />
                                            ) : (
                                                getFileIcon(
                                                    item.mimeType,
                                                    item.name,
                                                    20,
                                                )
                                            )}
                                        </div>
                                        <div className="list-col-name list-item-name">
                                            {item.name}
                                            {item.shareEnabled && (
                                                <Link
                                                    size={12}
                                                    className="list-share-badge"
                                                    title="Shared"
                                                />
                                            )}
                                        </div>
                                        <div className="list-col-type list-item-type">
                                            {item.type === "folder"
                                                ? "Folder"
                                                : item.mimeType
                                                      ?.split("/")[1]
                                                      ?.toUpperCase() ||
                                                  getFileType(
                                                      item.mimeType,
                                                      item.name,
                                                  ).toUpperCase()}
                                        </div>
                                        <div className="list-col-size list-item-meta">
                                            {item.type === "file"
                                                ? formatBytes(item.size)
                                                : "—"}
                                        </div>
                                        <div className="list-col-date list-item-meta">
                                            {formatDate(item.createdAt)}
                                        </div>
                                        <div
                                            className="list-col-actions list-item-actions"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                className="list-menu-btn"
                                                onClick={(e) =>
                                                    openMenuFromBtn(e, item)
                                                }
                                                title="More options"
                                            >
                                                <MoreHorizontal size={15} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </main>

            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={getMenuItems(contextMenu.item)}
                    onClose={() => setContextMenu(null)}
                />
            )}

            {modal?.type === "rename" && (
                <Modal title="Rename" onClose={() => setModal(null)}>
                    <input
                        className="modal-input"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleRename()}
                        autoFocus
                    />
                    <div className="modal-footer">
                        <button
                            className="btn-secondary"
                            onClick={() => setModal(null)}
                        >
                            Cancel
                        </button>
                        <button className="btn-primary" onClick={handleRename}>
                            Rename
                        </button>
                    </div>
                </Modal>
            )}
            {modal?.type === "newFolder" && (
                <Modal title="New Folder" onClose={() => setModal(null)}>
                    <input
                        className="modal-input"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) =>
                            e.key === "Enter" && handleNewFolder()
                        }
                        placeholder="Folder name"
                        autoFocus
                    />
                    <div className="modal-footer">
                        <button
                            className="btn-secondary"
                            onClick={() => setModal(null)}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleNewFolder}
                        >
                            Create
                        </button>
                    </div>
                </Modal>
            )}
            {modal?.type === "move" && (
                <FolderPicker
                    excludeId={modal.item._id}
                    onSelect={handleMoveSelect}
                    onClose={() => setModal(null)}
                />
            )}
            {modal?.type === "share" && (
                <Modal title="Share File" onClose={() => setModal(null)}>
                    <div className="share-modal">
                        <div className="share-filename">{modal.item.name}</div>
                        {shareInfo?.enabled && shareInfo?.token ? (
                            <>
                                <div className="share-status active">
                                    <Link size={16} /> Link active — anyone with
                                    this link can view & download
                                </div>
                                <div className="share-link-box">
                                    <input
                                        readOnly
                                        value={getShareUrl(shareInfo.token)}
                                        className="share-link-input"
                                    />
                                    <button
                                        className="btn-copy"
                                        onClick={handleCopyShareLink}
                                    >
                                        Copy
                                    </button>
                                </div>
                                <button
                                    className="btn-danger-sm"
                                    onClick={handleRevokeShare}
                                >
                                    Revoke link
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="share-status">
                                    No active share link
                                </div>
                                <button
                                    className="btn-primary"
                                    onClick={handleGenerateShare}
                                >
                                    Generate share link
                                </button>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
        </div>
    );
}
